/**
 * Central theme registry — single source of truth for all themes.
 *
 * Adding a new theme means:
 *   1. Add a new entry here with its `mode`, PrimeReact theme path, and Highcharts options.
 *   2. Add a token CSS file at `src/css/theme/tokens/<name>.css` with [data-theme="<name>"] { ... }.
 *   3. Include it in `src/css/theme/index.css`.
 *
 * That's it — the `useThemePreference`, `usePrimeReactTheme`, and `chartTheme` helpers
 * all pick up the new theme automatically.
 */

export type ThemePreference = 'light' | 'dark' | 'midnight' | 'browser';
export type ResolvedTheme = 'light' | 'dark' | 'midnight';
export type ThemeMode = 'light' | 'dark';

export interface ThemeDefinition {
    /** Whether this theme is light-ish or dark-ish (drives shared [data-mode="dark"] overrides) */
    mode: ThemeMode;
    /** PrimeReact theme CSS path — served from /public/primereact-themes/ */
    primereact: string;
    /** Display label for the settings UI */
    label: string;
    /** Short description for the settings UI */
    description: string;
    /** Icon (FontAwesome class) for the settings UI card */
    icon: string;
}

export const THEMES: Record<ResolvedTheme, ThemeDefinition> = {
    light: {
        mode: 'light',
        primereact: 'bootstrap4-light-blue',
        label: 'Light',
        description: 'Always use light theme',
        icon: 'fal fa-sun fa-fw',
    },
    dark: {
        mode: 'dark',
        primereact: 'bootstrap4-dark-blue',
        label: 'Dark',
        description: 'Grey dark theme',
        icon: 'fal fa-moon fa-fw',
    },
    midnight: {
        mode: 'dark',
        primereact: 'bootstrap4-dark-blue',
        label: 'Midnight',
        description: 'Blue dark theme',
        icon: 'fal fa-stars fa-fw',
    },
};

export const PREFERENCE_OPTIONS: Array<{
    value: ThemePreference;
    label: string;
    description: string;
    icon: string;
}> = [
    { value: 'light', label: 'Light', description: 'Always use light theme', icon: 'fal fa-sun fa-fw' },
    { value: 'dark', label: 'Dark', description: 'Grey dark theme', icon: 'fal fa-moon fa-fw' },
    { value: 'midnight', label: 'Midnight', description: 'Blue dark theme', icon: 'fal fa-stars fa-fw' },
    { value: 'browser', label: 'System', description: 'Follow your system setting', icon: 'fal fa-desktop fa-fw' },
];
